import { useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DotMenu from "../../DotMenu";
import Translate from "../../../utils/Translate";
import Avatar from "@mui/material/Avatar";
import { Edit, Clear } from "@mui/icons-material";
import { TextField } from "@mui/material";
import { useApi } from "../../../utils/Api";
import { useSnackbar } from "notistack";
import { stringToColor } from "../../../utils/formatString";
import { validatePhoneNumber } from "../../../utils/phoneNumber";
import PersonCrudDialog from "../../actors/crud/PersonCrudDialog";
import ErrorMessage from "../../ErrorMessage";

function getDotMenuActions(onCrudPerson, onRemoveRecipient) {
  const menuItems = [
    {
      icon: <Edit />,
      text: Translate.get("EditPersonalInfo"),
      onClick: onCrudPerson,
    },
    {
      icon: <Clear />,
      text: Translate.get("RemoveRecipient"),
      onClick: onRemoveRecipient,
    },
  ];
  return menuItems;
}

const SendSms = ({ receiverActors, courseId, companyActorId, companyName, onSuccess }) => {
  const sortedReceiverActors = [...receiverActors].sort((a, b) => a.actorName.localeCompare(b.actorName));

  const api = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [showPersonCrudForActorId, setShowPersonCrudForActorId] = useState(null);
  const [anyActorUpdated, setAnyActorUpdated] = useState(false);
  const [message, setMessage] = useState({ value: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [receivers, setReceivers] = useState(sortedReceiverActors);

  async function sendSms() {
    const receiverActorIds = [];
    receivers.map((row, index) => {
      if (isValidNumber(row?.mobilePhone?.actorPhoneNumber)) {
        receiverActorIds.push(row.actorId);
      }
    });
    if (receiverActorIds.length === 0 || !message.value) {
      return false;
    }
    const outData = {
      toActorIds: receiverActorIds,
      message: message.value,
      courseId: courseId,
    };
    await submit(outData);
  }

  async function submit(outData) {
    setIsLoading(true);

    try {
      const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}sms/freetext`, outData);
      if (response.isSuccessful) {
        enqueueSnackbar(Translate.get("Sent"), {
          variant: "success",
          autoHideDuration: 3000,
        });
        onSuccess();
        window.history.back();
      }
    } finally {
      setIsLoading(false);
    }
  }

  const isValidNumber = (numChk) => {
    if (!numChk) {
      return false;
    }

    return validatePhoneNumber(numChk);
  };

  const removeReceiver = (id) => {
    const filter = receivers.filter((x) => x.actorId !== id);
    setReceivers(filter);
  };

  const actorSaved = (updatedActor) => {
    setAnyActorUpdated(true);
    setReceivers(receivers.map((actor) => (actor.actorId === updatedActor.actorId ? updatedActor : actor)));
    setShowPersonCrudForActorId(null);
  };

  return (
    <>
      <DialogContent
        dividers={true}
        sx={{
          minWidth: { xs: "100%", sm: 550 },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        {courseId != null && (
          <DialogContentText className="dialogSubtitle">
            {Translate.get("CourseParticipantsValidPhoneNumber")}
          </DialogContentText>
        )}
        <div className="smallHeader topBottom16">
          {Translate.get("EmailReceivers")} {companyName} ({receivers?.length})
        </div>
        {receivers.map((row, index) => (
          <div key={index} className="stdList">
            <div className="tableAvatar">
              <Avatar
                alt={row.actorName}
                src={row.profilePictureUrl}
                sx={{ bgcolor: stringToColor(row.actorName) }}
              ></Avatar>
              <div>
                <div className="tableAvatarTitle bold">{row.actorName}</div>
                {row?.mobilePhone?.actorPhoneNumber && isValidNumber(row.mobilePhone.actorPhoneNumber) ? (
                  row.mobilePhone.actorPhoneNumber
                ) : (
                  <ErrorMessage
                    onClick={() => setShowPersonCrudForActorId(row.actorId)}
                    error={Translate.get("MobileMissingOrInvalid") + " " + (row?.mobilePhone?.actorPhoneNumber || "")}
                  />
                )}
              </div>
            </div>
            <div>
              <DotMenu
                content={getDotMenuActions(
                  () => setShowPersonCrudForActorId(row.actorId),
                  () => removeReceiver(row.actorId),
                )}
              />
            </div>
          </div>
        ))}
        <div className="smallHeader topBottom16">{Translate.get("Message")}</div>

        <TextField
          multiline
          rows={4}
          fullWidth
          label={Translate.get("Message")}
          slotProps={{
            htmlInput: {
              maxLength: 300,
            },
          }}
          onChange={(event, value) => {
            setMessage({ value: event.target.value });
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (anyActorUpdated) {
              onSuccess();
            }
            window.history.back();
          }}
        >
          {Translate.get("Cancel")}
        </Button>
        <Button
          className="stdLightLoader"
          loading={isLoading}
          disabled={!message || message.value === ""}
          onClick={() => sendSms()}
        >
          {Translate.get("Send")}
        </Button>
      </DialogActions>
      {showPersonCrudForActorId && (
        <PersonCrudDialog
          open={true}
          personCrudProps={{
            actorId: showPersonCrudForActorId,
            isEditOnly: true,
            parentActorId: companyActorId,
            onActorSaved: actorSaved,
            onClose: () => setShowPersonCrudForActorId(null),
          }}
        />
      )}
    </>
  );
};
export default SendSms;
